import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import { MAX_LESSONS_IN_VIEW } from '@platform/schema/chat';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ChatPanel } from '../authoring/ChatPanel';
import { useEditing } from '../authoring/editing';
import { UndoRedo } from '../authoring/UndoRedo';

import { reviewQuery } from './sources';

/**
 * One question about several lessons, and the edits it comes back with — GEN-70.
 *
 * The panel itself is the workspace's. What is here is what the workspace got for free by
 * being about one lesson: reading the lessons picked, and giving the edits somewhere to
 * land with a way back out. `reviewQuery` per id because the list row is deliberately
 * thin and a proposal's "Was" column is read from the scene text.
 *
 * Applying is `useEditing.save`, so an edit applied here is a hand edit and ⌘Z takes it
 * back — a bulk apply with no way back is what `docs/decisions/planner-proposals.md`
 * refuses.
 */
export function LessonsChat({ lessonIds }: { lessonIds: readonly string[] }) {
  const queries = useQueryClient();
  const picked = lessonIds.slice(0, MAX_LESSONS_IN_VIEW);

  /**
   * `combine` rather than mapping the results afterwards: it is memoised on the queries'
   * own state, so `lessons` keeps its identity between renders. A fresh array each time
   * walks up through `useEditing.save` into `ChatPanel`, whose `Transcript` is `memo`'d
   * precisely so that typing does not re-derive every past proposal's diff.
   */
  const { lessons, loading } = useQueries({
    queries: picked.map(reviewQuery),
    combine: (asked) => ({
      lessons: asked.flatMap((one) =>
        one.data === undefined ? [] : [one.data],
      ),
      loading: asked.some((one) => one.isPending),
    }),
  });

  /**
   * Only the lesson that moved, and the list behind the panel. Invalidating all eight
   * `review` keys made one applied proposal nine refetches of a payload carrying every
   * scene, item and question of a lesson — and eight of them had not changed.
   */
  const refresh = useCallback(
    async (lessonId: string): Promise<void> => {
      await Promise.all([
        queries.invalidateQueries({ queryKey: reviewQuery(lessonId).queryKey }),
        queries.invalidateQueries({ queryKey: ['lessons'] }),
      ]);
    },
    [queries],
  );

  const edit = useEditing(lessons, refresh);

  // A published lesson refuses every write, so offering an apply that will be refused is
  // worse than saying so — the same rule the workspace applies one lesson at a time.
  const published = lessons.filter((one) => one.status !== 'DRAFT');

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {lessonIds.length > MAX_LESSONS_IN_VIEW && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Asking about the first {String(MAX_LESSONS_IN_VIEW)} of the{' '}
          {String(lessonIds.length)} you selected. More than that is a review
          pass rather than a question.
        </Alert>
      )}

      {published.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {published.length === lessons.length
            ? 'These lessons are published, so nothing can be applied to them. Move one back to draft to edit it.'
            : `${String(published.length)} of these are published, so nothing can be applied to those. Move one back to draft to edit it.`}
        </Alert>
      )}

      <UndoRedo
        undoLabel={edit.back?.label}
        redoLabel={edit.forward?.label}
        disabled={edit.pending}
        scope="One edit at a time, across the lessons you picked"
        onUndo={edit.undo}
        onRedo={edit.redo}
      />

      {loading && <LinearProgress sx={{ mt: 1 }} />}

      <ChatPanel
        subject={{
          kind: 'lesson',
          lessonIds: picked,
          lessons,
          onApply: edit.save,
          onApplyAll: edit.saveAll,
        }}
        disabled={edit.pending || published.length === lessons.length}
      />
    </Paper>
  );
}
